import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Power } from 'lucide-react';
import { usePortal } from '../../context/PortalContext';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../services/api';
import UserAvatar from '../common/UserAvatar';
import LogoutConfirmModal from '../common/LogoutConfirmModal';
import PortalRegisterModal from './PortalRegisterModal';
import PortalLoginModal from './PortalLoginModal';
import CountrySelector from './CountrySelector';

const PublicHeader = ({ activeSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [logoutTarget, setLogoutTarget] = useState(null); // 'portal' | 'app' | null
  const location = useLocation();
  const navigate = useNavigate();
  const { portalUser, portalLogout } = usePortal();
  const { logout: appLogout, user: appUser } = useAuth();

  // Check if user is already logged in (has valid session)
  const sessionInfo = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        const user = JSON.parse(userStr);
        // Check if token might be expired (basic check - real validation happens on API call)
        // JWT payloads are base64url — decode with the proper alphabet
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        const isExpired = payload.exp * 1000 < Date.now();
        
        if (!isExpired) {
          return {
            isLoggedIn: true,
            userName: user.full_name || user.username,
            userEmail: user.email || '',
            userRole: user.role,
            isSuperuser: user.is_superuser || false,
            isDemo: payload.is_demo || false
          };
        }
      }
    } catch (e) {
      // Invalid token or user data - treat as logged out
    }
    return { isLoggedIn: false };
  }, [portalUser, appUser]);

  useEffect(() => {
    let frame = null;
    const handleScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const nextScrolled = window.scrollY > 10;
        setScrolled(current => current === nextScrolled ? current : nextScrolled);
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  const handlePortalLogout = async () => {
    setLogoutLoading(true);
    try {
      await portalLogout();
      await new Promise(r => setTimeout(r, 400));
      navigate('/');
    } catch (e) {
      // Logout must never leave the user stuck — clear local state regardless
      localStorage.removeItem('portalToken');
      localStorage.removeItem('portalRefreshToken');
      navigate('/');
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleAppLogout = async () => {
    setLogoutLoading(true);
    try {
      await appLogout();
      await new Promise(r => setTimeout(r, 400));
      navigate('/');
    } catch (e) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      navigate('/');
    } finally {
      setLogoutLoading(false);
    }
  };

  // Go to App: for users with an active demo, ensure demo credentials are
  // available so /login shows the app login form pre-filled as the demo user
  const handleGoToApp = async () => {
    setMobileMenuOpen(false);
    try {
      if (!sessionStorage.getItem('demoData') && portalUser?.tenant_id) {
        const portalToken = localStorage.getItem('portalToken') || localStorage.getItem('token');
        const resp = await fetch(`${API_URL}/api/demo/credentials/${portalUser.tenant_id}`, {
          headers: portalToken ? { 'Authorization': `Bearer ${portalToken}` } : {},
        });
        if (resp.ok) {
          const data = await resp.json();
          const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
          sessionStorage.setItem('demoData', JSON.stringify({
            facility_name: data.facility_name || 'Demo Facility',
            expires_in_hours: expiresAt ? Math.max(1, Math.ceil((expiresAt - Date.now()) / 3600000)) : 24,
            credentials: (data.credentials || []).map((c) => ({
              role: c.role,
              display_name: c.display_name,
              username: c.username,
              password: c.password,
            })),
          }));
        }
      }
    } catch (e) {
      // Fall through to the login form; the demo box will simply be absent
    }
    navigate('/login');
  };

  // --- Session display helpers ---------------------------------------------
  const isPortalSession = !!portalUser;
  const avatarUser = isPortalSession
    ? { full_name: portalUser?.name || portalUser?.email, email: portalUser?.email, profile_picture: portalUser?.profile_picture }
    : (appUser || { full_name: sessionInfo.userName, email: sessionInfo.userEmail });
  const userCardTarget = isPortalSession
    ? (portalUser?.is_superuser ? null : '/portal/dashboard')
    : '/app/profile';
  // First name for the greeting — blank when the only identifier is an email/username
  const greetingName = (() => {
    const raw = (isPortalSession ? portalUser?.name : sessionInfo.userName) || '';
    const first = raw.trim().split(' ')[0];
    return first && !first.includes('@') ? first : '';
  })();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Our Story', href: '/our-story' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Patient Portal', href: '/patient-portal' },
    // Tutorials page temporarily disabled — still has mockup data. Re-enable when ready.
    // { name: 'Tutorials', href: '/tutorials' },
  ];

  const isActive = (href) => {
    if (activeSection) {
      if (href === '/') return activeSection === 'home';
      if (href === '/pricing') return activeSection === 'pricing';
    }
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Rendered into <body> rather than in place: any ancestor with a transform,
  // filter, backdrop-filter or will-change becomes the containing block for
  // `position: fixed`, which made the header scroll away with the page on some
  // mobile browsers. As a direct child of <body> nothing can re-anchor it.
  return createPortal(
    <header
      className={`public-header fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-lg shadow-teal-500/5 border-b border-gray-200/70 dark:border-gray-800'
        : 'bg-white/0 dark:bg-gray-900/0 backdrop-blur-sm'
        }`}
    >
      {/* Ultramodern gradient accent line at the very top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500 opacity-70"></div>

      {/* Gradient fade effect at bottom of header when scrolled */}
      {scrolled && (
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/20 to-transparent"></div>
      )}
      
      <nav className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="w-full py-2.5 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex flex-col group shrink-0">
            <div className="flex items-baseline">
              <span className={`font-bold leading-none bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent tracking-tight transition-all duration-300 ${scrolled ? 'text-2xl' : 'text-3xl md:text-4xl'}`} style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>
                Ospito
              </span>
              <span className={`font-bold leading-none bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent -ml-0.5 -translate-y-1.5 inline-block transition-all duration-300 ${scrolled ? 'text-lg' : 'text-xl md:text-2xl'}`} style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>
                +
              </span>
            </div>
            <div className={`text-gray-500 font-medium leading-tight -mt-1 tracking-wide transition-all duration-300 ${scrolled ? 'text-xs' : 'text-sm'}`}>Hospital Management System</div>
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex md:items-center md:space-x-1 absolute left-1/2 -translate-x-1/2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="relative px-4 py-2 group"
              >
                <span
                  className={`relative z-10 text-sm font-semibold transition-colors duration-200 ${isActive(item.href)
                    ? 'text-teal-600 dark:text-teal-400'
                    : 'text-gray-700 dark:text-gray-300 group-hover:text-teal-600 dark:group-hover:text-teal-400'
                    }`}
                >
                  {item.name}
                </span>
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full" />
                )}
                <span className="absolute inset-0 rounded-lg bg-teal-50 scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out" />
              </Link>
            ))}
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden md:flex md:items-center md:space-x-3">
            <CountrySelector variant="desktop" />
            {portalUser || sessionInfo.isLoggedIn ? (
              /* Logged-in user (portal or app) — greeting + direct logout icon */
              <div className="flex items-center gap-2.5">
                {userCardTarget ? (
                  <button
                    onClick={() => navigate(userCardTarget)}
                    className="text-xs font-semibold text-gray-700 dark:text-gray-200 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    title="Account"
                  >
                    Hi{greetingName ? `, ${greetingName}` : ''}
                  </button>
                ) : (
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    Hi{greetingName ? `, ${greetingName}` : ''}
                  </span>
                )}
                <button
                  onClick={() => setLogoutTarget(isPortalSession ? 'portal' : 'app')}
                  disabled={logoutLoading}
                  title="Log out"
                  aria-label="Log out"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Power className="w-4 h-4" />
                </button>
              </div>
            ) : !sessionInfo.isLoggedIn ? (
              /* Login + Register CTAs — shown when no portal user and no app session */
              <>
              <button
                onClick={() => setLoginModalOpen(true)}
                className="group relative inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold tracking-tight text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-white/5 backdrop-blur-md border border-gray-300/60 dark:border-white/10 transition-all duration-300 hover:border-gray-900/30 dark:hover:border-white/30 hover:bg-white/80 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white"
              >
                Login
              </button>
              <button
                onClick={() => setRegisterModalOpen(true)}
                className="group relative inline-flex items-center px-3 py-1.5 overflow-hidden rounded-lg text-xs font-bold tracking-tight text-white transition-all duration-300 hover:shadow-md hover:shadow-teal-500/20"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-teal-500 to-cyan-600" />
                <span className="absolute inset-0 bg-gradient-to-r from-teal-600 to-cyan-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center">
                  Register
                  <svg className="ml-1 h-3 w-3 transform group-hover:translate-x-0.5 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
              </>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-xl text-gray-700 dark:text-gray-200 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-all duration-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="public-mobile-menu"
            >
              <span className="sr-only">{mobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          id="public-mobile-menu"
          className={`md:hidden max-h-0 overflow-hidden transition-all duration-300 ease-in-out bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 ${mobileMenuOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'opacity-0'
            }`}
        >
          <div className="pb-4 pt-2 px-2 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${isActive(item.href)
                  ? 'text-teal-600 dark:text-teal-400 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20'
                  }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-3">
              <CountrySelector variant="mobile" />
            </div>
            <div className="pt-3 space-y-2">
              {sessionInfo.isLoggedIn && (
                <Link
                  to="/app"
                  className="flex items-center justify-center gap-2  px-4 py-3 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-lg shadow-emerald-500/30"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                    {sessionInfo.userName?.charAt(0).toUpperCase()}
                  </span>
                  Go to App
                </Link>
              )}
              {portalUser ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-2  rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800">
                    <div className="relative shrink-0">
                      <UserAvatar user={{ full_name: portalUser?.name || portalUser?.email, email: portalUser?.email, profile_picture: portalUser?.profile_picture }} size="sm" />
                      <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-gradient-to-br from-teal-500 to-purple-500 ring-2 ring-white dark:ring-gray-900" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{portalUser.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{portalUser.email}</p>
                    </div>
                  </div>
                  {!portalUser.is_superuser && (
                    <Link
                      to="/portal/dashboard"
                      className="block text-center px-4 py-3  text-base font-semibold rounded-xl text-white bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 transition-all duration-200 shadow-lg shadow-teal-500/30"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Messages
                    </Link>
                  )}
                  {portalUser.can_access_app && (
                    <button
                      onClick={handleGoToApp}
                      className="block w-full text-center px-4 py-3  text-base font-semibold rounded-xl text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-lg shadow-emerald-500/30"
                    >
                      Go to App
                    </button>
                  )}
                  <button
                    onClick={() => { setLogoutTarget('portal'); setMobileMenuOpen(false); }}
                    disabled={logoutLoading}
                    className="flex items-center justify-center gap-2 w-full text-center px-4 py-3  text-base font-semibold rounded-xl text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {logoutLoading && (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {logoutLoading ? 'Signing out…' : 'Sign Out'}
                  </button>
                </>
              ) : sessionInfo.isLoggedIn ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-2  rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800">
                    <div className="relative shrink-0 rounded-full p-[2px] bg-gradient-to-br from-purple-400/70 via-teal-300/60 to-amber-300/70 dark:from-purple-500/60 dark:via-teal-400/50 dark:to-amber-400/60">
                      <div className="rounded-full bg-white dark:bg-gray-900 p-[2px]">
                        <UserAvatar user={appUser || { full_name: sessionInfo.userName }} size="sm" />
                      </div>
                      <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-gradient-to-br from-teal-500 to-purple-500 ring-2 ring-white dark:ring-gray-900" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{sessionInfo.userName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{sessionInfo.userRole}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setLogoutTarget('app'); setMobileMenuOpen(false); }}
                    disabled={logoutLoading}
                    className="flex items-center justify-center gap-2 w-full text-center px-4 py-3  text-base font-semibold rounded-xl text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {logoutLoading && (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {logoutLoading ? 'Signing out…' : 'Sign Out'}
                  </button>
                </>
              ) : !sessionInfo.isLoggedIn ? (
                <>
                  <button
                    onClick={() => { setLoginModalOpen(true); setMobileMenuOpen(false); }}
                    className="w-full text-center px-4 py-3 text-base font-semibold rounded-xl text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => { setRegisterModalOpen(true); setMobileMenuOpen(false); }}
                    className="w-full text-center px-4 py-3 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 transition-all duration-200 shadow-lg shadow-teal-500/30"
                  >
                    Register
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </nav>

      {/* Portal Register Modal - rendered outside header to avoid backdrop-blur containing block issue */}
      {createPortal(
        <PortalRegisterModal
          isOpen={registerModalOpen}
          onClose={() => setRegisterModalOpen(false)}
          onSwitchToLogin={() => { setRegisterModalOpen(false); setLoginModalOpen(true); }}
        />,
        document.body
      )}

      {/* Portal Login Modal */}
      {createPortal(
        <PortalLoginModal
          isOpen={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          onSwitchToRegister={() => { setLoginModalOpen(false); setRegisterModalOpen(true); }}
        />,
        document.body
      )}

      {/* Logout Confirmation Modal */}
      {createPortal(
        <LogoutConfirmModal
          isOpen={!!logoutTarget}
          user={avatarUser}
          loading={logoutLoading}
          onConfirm={() => {
            setLogoutTarget(null);
            if (logoutTarget === 'portal') {
              handlePortalLogout();
            } else {
              handleAppLogout();
            }
          }}
          onCancel={() => setLogoutTarget(null)}
        />,
        document.body
      )}
    </header>,
    document.body
  );
};

export default PublicHeader;
